import Link from "next/link";
import { useRouter } from "next/router";

import { useCallback, useMemo } from "react";
import { Popover } from "react-bootstrap";

import { format } from "date-fns";
import { isArray, isNumber, orderBy, uniqBy } from "lodash";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import AddToWatchlistButton from "components/AddToWatchlistButton";
import CustomCard from "components/CustomCard";
import EntityAnalyticsChart from "components/EntityAnalyticsChart";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import StickyOverlayTrigger from "components/StickyOverlayTrigger";
import Table from "components/Table";
import { useApi } from "hooks/useApi";
import { useGlobalState } from "hooks/useGlobalState";
import { baseFetcher } from "utils/baseFetcher";
import { maxBy, meanBy, medianBy, minBy } from "utils/common";

import PeersTradeComparisonLoading from "../Loading";

export const mnaPeersComparisonUrl = (entityId: any) => {
  return `/api/v3/merging-acquisitions/${entityId}/peers-comparison`;
};

export const columns = [
  {
    title: "Company",
    dataIndex: "pretty_name",
    key: "pretty_name",
    style: {
      minWidth: 200,
    },
    render: (value: string, record: any) => {
      if (!record) return null;

      if (record.id) {
        return (
          <div className="flex">
            <StickyOverlayTrigger
              placement="right"
              overlay={
                <Popover
                  className={twMerge(
                    "left-[-9999px]", // add intitial postion outisde of viewport to fix flickering
                    "bg-white shadow-lg rounded",
                    "min-w-[300px]",
                    "border border-solid border-gray-200",
                    "font-roboto",
                    "z-[1999]"
                  )}
                >
                  <Popover.Body>
                    <div className="w-full flex justify-between items-center mb-[15px]">
                      <span className="text-[16px] font-medium line-clamp-2">
                        {value}
                      </span>
                    </div>
                    <EntityAnalyticsChart
                      slug={record.id}
                      titleClassName="font-bold"
                      priceClassName="leading-[1.4]"
                    />
                    <AddToWatchlistButton entity={record}>
                      {({ addToWatchlist, isWatching }) => (
                        <span
                          className={twMerge(
                            "text-[12px] text-right",
                            isWatching ? "text-jade" : "text-smart-teal",
                            "cursor-pointer"
                          )}
                          onClick={addToWatchlist}
                        >
                          <FontAwesomeIcon
                            icon={isWatching ? faCheck : faPlus}
                          />{" "}
                          {isWatching ? "Added" : "Add"} to Watchlist
                        </span>
                      )}
                    </AddToWatchlistButton>
                  </Popover.Body>
                </Popover>
              }
            >
              <Link
                target="_blank"
                className="underline line-clamp-1"
                href={`/entities/${record.id}`}
              >
                {`${value} (${record.exchange_code})` || "-"}
              </Link>
            </StickyOverlayTrigger>
          </div>
        );
      }

      return value;
    },
  },
  {
    title: "Market Cap (USDm)",
    dataIndex: "basic_stats.market_value_usd",
    key: "basic_stats.market_value_usd",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value ? `${numeral(value).format("0,0")}%` : "-";
      }
      return value && value > 0 ? numeral(value).format("0,0") : "-";
    },
    className: "text-right",
    headerClassName: "text-right",
  },
  {
    title: "EV/Sales",
    dataIndex: "basic_stats.ev_sales",
    key: "basic_stats.ev_sales",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value && isNumber(value)
          ? `${numeral(value).format("0,0")}%`
          : "-";
      }
      return value && value > 0 ? numeral(value).format("0,0.[0]") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "EV/EBITDA",
    dataIndex: "basic_stats.ev_ebitda",
    key: "basic_stats.ev_ebitda",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value && isNumber(value)
          ? `${numeral(value).format("0,0.0")}%`
          : "-";
      }
      return value && value > 0 ? numeral(value).format("0,0.[0]") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "P/E",
    dataIndex: "basic_stats.pe",
    key: "basic_stats.pe",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value && isNumber(value)
          ? `${numeral(value).format("0,0")}%`
          : "-";
      }
      return value && value > 0 ? numeral(value).format("0,0.[0]") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "P/B",
    dataIndex: "basic_stats.pb",
    key: "basic_stats.pb",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value && isNumber(value)
          ? `${numeral(value).format("0,0")}%`
          : "-";
      }
      return value && value > 0 ? numeral(value).format("0,0.[0]") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },

  //Additional
  {
    title: "Revenue (USDm)",
    dataIndex: "factset_stats.revenue",
    key: "factset_stats.revenue",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value && isNumber(value)
          ? `${numeral(value).format("0,0")}%`
          : "-";
      }
      return value ? numeral(value).format("0,0") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "Revenue Growth (1Y%)",
    dataIndex: "factset_stats.revenue_1y",
    key: "factset_stats.revenue_1y",
    render: (value: number) => {
      return value && isNumber(value) ? numeral(value).format("0,0.0%") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "Revenue Growth (3Y%)",
    dataIndex: "factset_stats.revenue_3y_cagr",
    key: "factset_stats.revenue_3y_cagr",
    render: (value: number) => {
      return value && isNumber(value) ? numeral(value).format("0,0.0%") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "EBITDA (USDm)",
    dataIndex: "factset_stats.ebitda",
    key: "factset_stats.ebitda",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value ? `${numeral(value).format("0,0")}%` : "-";
      }
      return value ? numeral(value).format("0,0") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "EBITDA Growth (1Y%)",
    dataIndex: "factset_stats.ebitda_1y",
    key: "factset_stats.ebitda_1y",
    render: (value: number) => {
      return value && isNumber(value) ? numeral(value).format("0,0.0%") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "EBITDA Growth (3Y%)",
    dataIndex: "factset_stats.ebitda_3y_cagr",
    key: "factset_stats.ebitda_3y_cagr",
    render: (value: number) => {
      return value && isNumber(value) ? numeral(value).format("0,0.0%") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "Net Income (USDm)",
    dataIndex: "factset_stats.net_income",
    key: "factset_stats.net_income",
    render: (value: number, record: any) => {
      if (record.type === "diff") {
        return value && isNumber(value)
          ? `${numeral(value).format("0,0")}%`
          : "-";
      }
      return value && isNumber(value) ? numeral(value).format("0,0") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "Net Income Growth (1Y%)",
    dataIndex: "factset_stats.net_income_1y",
    key: "factset_stats.net_income_1y",
    render: (value: number) => {
      return value && isNumber(value) ? numeral(value).format("0,0.0%") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
  {
    title: "Net Income Growth (3Y%)",
    dataIndex: "factset_stats.net_income_3y_cagr",
    key: "factset_stats.net_income_3y_cagr",
    render: (value: number) => {
      return value && isNumber(value) ? numeral(value).format("0,0.0%") : "-";
    },
    headerClassName: "text-right",
    className: "text-right",
  },
];

export const usePeersComparisonData = (entityId: string, embed?: boolean) => {
  const [mnaPeersComparisonLoading, setMnaPeersComparisonLoading] =
    useGlobalState(`mnaPeersComparisonLoading-${entityId}`, true);
  const [_isExpanded, setIsExpanded] = useGlobalState(`mnaPeersIsExpanded`);

  const { data: rawComparisons, isLoading } = useApi(
    entityId ? mnaPeersComparisonUrl(entityId) : null,
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: !embed,
      onSuccess: (data: any) => {
        if (data?.status !== "enqueued") {
          setIsExpanded(true);
          setMnaPeersComparisonLoading(false);
        }
      },
    }
  );

  const calculateDiff = useCallback((a, b, ignoreZero?: boolean) => {
    if (!a || !b) {
      return "-";
    }

    if ((a < 0 || b < 0) && !ignoreZero) {
      return "-";
    }

    return (a / b - 1) * 100;
  }, []);

  const calculatePercentageDiff = useCallback((a, b) => {
    if (!a || !b) return "-";

    return a - b;
  }, []);

  const comparisons = useMemo(() => {
    if (!rawComparisons || !isArray(rawComparisons)) return [];

    const peers = uniqBy(
      rawComparisons.filter((comparison: any) => !comparison?.is_target),
      (peer) => peer.pretty_name || 0
    );
    const target = rawComparisons.find(
      (comparison: any) => comparison?.is_target
    );
    const basicCalculatedData = [
      {
        pretty_name: "Peers Average",
        type: "calculated",
        basic_stats: {
          market_value_usd: meanBy(
            peers,
            (peer: any) => peer?.basic_stats?.market_value_usd || 0,
            true
          ),
          ev_sales: meanBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_sales || 0,
            true
          ),
          ev_ebitda: meanBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_ebitda || 0,
            true
          ),
          pe: meanBy(peers, (peer: any) => peer?.basic_stats?.pe || 0, true),
          pb: meanBy(peers, (peer: any) => peer?.basic_stats?.pb || 0, true),
        },
        factset_stats: {
          revenue: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue || 0
          ),
          revenue_1y: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_1y || 0
          ),
          revenue_3y_cagr: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_3y_cagr || 0
          ),
          ebitda: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda || 0
          ),
          ebitda_1y: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_1y || 0
          ),
          ebitda_3y_cagr: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_3y_cagr || 0
          ),
          net_income: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income || 0
          ),
          net_income_1y: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_1y || 0
          ),
          net_income_3y_cagr: meanBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_3y_cagr || false
          ),
        },
      },
      {
        pretty_name: "Peers Median",
        type: "calculated",
        basic_stats: {
          market_value_usd: medianBy(
            peers,
            (peer: any) => peer?.basic_stats?.market_value_usd || 0,
            true
          ),
          ev_sales: medianBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_sales || 0,
            true
          ),
          ev_ebitda: medianBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_ebitda || 0,
            true
          ),
          pe: medianBy(peers, (peer: any) => peer?.basic_stats?.pe || 0, true),
          pb: medianBy(peers, (peer: any) => peer?.basic_stats?.pb || 0, true),
        },
        factset_stats: {
          revenue: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue || 0
          ),
          revenue_1y: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_1y || 0
          ),
          revenue_3y_cagr: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_3y_cagr || 0
          ),
          ebitda: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda || 0
          ),
          ebitda_1y: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_1y || 0
          ),
          ebitda_3y_cagr: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_3y_cagr || 0,
            false
          ),
          net_income: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income || 0
          ),
          net_income_1y: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_1y || 0
          ),
          net_income_3y_cagr: medianBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_3y_cagr || 0
          ),
        },
      },
      {
        pretty_name: "Peers Max",
        type: "calculated",
        basic_stats: {
          market_value_usd: maxBy(
            peers,
            (peer: any) => peer?.basic_stats?.market_value_usd || 0
          ),
          ev_sales: maxBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_sales || 0
          ),
          ev_ebitda: maxBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_ebitda || 0
          ),
          pe: maxBy(peers, (peer: any) => peer?.basic_stats?.pe || 0),
          pb: maxBy(peers, (peer: any) => peer?.basic_stats?.pb || 0),
        },
        factset_stats: {
          revenue: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue || 0
          ),
          revenue_1y: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_1y || 0
          ),
          revenue_3y_cagr: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_3y_cagr || 0
          ),
          ebitda: maxBy(peers, (peer: any) => peer?.factset_stats?.ebitda || 0),
          ebitda_1y: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_1y || 0
          ),
          ebitda_3y_cagr: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_3y_cagr || 0
          ),
          net_income: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income || 0
          ),
          net_income_1y: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_1y || 0
          ),
          net_income_3y_cagr: maxBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_3y_cagr || 0
          ),
        },
      },
      {
        pretty_name: "Peers Min",
        type: "calculated",
        basic_stats: {
          market_value_usd: minBy(
            peers,
            (peer: any) => peer?.basic_stats?.market_value_usd || 0,
            true
          ),
          ev_sales: minBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_sales || 0,
            true
          ),
          ev_ebitda: minBy(
            peers,
            (peer: any) => peer?.basic_stats?.ev_ebitda || 0,
            true
          ),
          pe: minBy(peers, (peer: any) => peer?.basic_stats?.pe || 0, true),
          pb: minBy(peers, (peer: any) => peer?.basic_stats?.pb || 0, true),
        },
        factset_stats: {
          revenue: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue || 0
          ),
          revenue_1y: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_1y || 0
          ),
          revenue_3y_cagr: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.revenue_3y_cagr || 0
          ),
          ebitda: minBy(peers, (peer: any) => peer?.factset_stats?.ebitda || 0),
          ebitda_1y: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_1y || 0
          ),
          ebitda_3y_cagr: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.ebitda_3y_cagr || 0
          ),
          net_income: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income || 0
          ),
          net_income_1y: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_1y || 0
          ),
          net_income_3y_cagr: minBy(
            peers,
            (peer: any) => peer?.factset_stats?.net_income_3y_cagr || 0
          ),
        },
      },
    ];

    const diffCalculatedData = [
      {
        pretty_name: "Difference vs Peers Average",
        type: "diff",
        basic_stats: {
          market_value_usd: calculateDiff(
            target?.basic_stats?.market_value_usd?.toFixed(0),
            meanBy(
              peers,
              (peer: any) => peer?.basic_stats?.market_value_usd || 0,
              true
            )?.toFixed(0)
          ),
          ev_sales: calculateDiff(
            target?.basic_stats?.ev_sales?.toFixed(1),
            meanBy(
              peers,
              (peer: any) => peer?.basic_stats?.ev_sales || 0,
              true
            )?.toFixed(1)
          ),
          ev_ebitda: calculateDiff(
            target?.basic_stats?.ev_ebitda?.toFixed(1),
            meanBy(
              peers,
              (peer: any) => peer?.basic_stats?.ev_ebitda || 0,
              true
            )?.toFixed(1)
          ),
          pe: calculateDiff(
            target?.basic_stats?.pe?.toFixed(1),
            meanBy(
              peers,
              (peer: any) => peer?.basic_stats?.pe || 0,
              true
            )?.toFixed(1)
          ),
          pb: calculateDiff(
            target?.basic_stats?.pb?.toFixed(1),
            meanBy(
              peers,
              (peer: any) => peer?.basic_stats?.pb || 0,
              true
            )?.toFixed(1)
          ),
        },
        factset_stats: {
          revenue: calculateDiff(
            target?.factset_stats?.revenue?.toFixed(0),
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.revenue || 0
            )?.toFixed(0)
          ),
          revenue_1y: calculatePercentageDiff(
            target?.factset_stats?.revenue_1y,
            meanBy(peers, (peer: any) => peer?.factset_stats?.revenue_1y || 0)
          ),
          revenue_3y_cagr: calculatePercentageDiff(
            target?.factset_stats?.revenue_3y_cagr,
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.revenue_3y_cagr || 0
            )
          ),
          ebitda: calculateDiff(
            target?.factset_stats?.ebitda?.toFixed(0),
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.ebitda || 0
            )?.toFixed(0),
            true
          ),
          ebitda_1y: calculatePercentageDiff(
            target?.factset_stats?.ebitda_1y,
            meanBy(peers, (peer: any) => peer?.factset_stats?.ebitda_1y || 0)
          ),
          ebitda_3y_cagr: calculatePercentageDiff(
            target?.factset_stats?.ebitda_3y_cagr,
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.ebitda_3y_cagr || 0
            )
          ),
          net_income: calculateDiff(
            target?.factset_stats?.net_income?.toFixed(0),
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.net_income || 0
            )?.toFixed(0)
          ),
          net_income_1y: calculatePercentageDiff(
            target?.factset_stats?.net_income_1y,
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.net_income_1y || 0
            )
          ),
          net_income_3y_cagr: calculatePercentageDiff(
            target?.factset_stats?.net_income_3y_cagr,
            meanBy(
              peers,
              (peer: any) => peer?.factset_stats?.net_income_3y_cagr || 0
            )
          ),
        },
      },
      {
        pretty_name: "Difference vs Peers Median",
        type: "diff",
        basic_stats: {
          market_value_usd: calculateDiff(
            target?.basic_stats?.market_value_usd?.toFixed(0),
            medianBy(
              peers,
              (peer: any) => peer?.basic_stats?.market_value_usd || 0,
              true
            )?.toFixed(0)
          ),
          ev_sales: calculateDiff(
            target?.basic_stats?.ev_sales?.toFixed(1),
            medianBy(
              peers,
              (peer: any) => peer?.basic_stats?.ev_sales || 0,
              true
            )?.toFixed(1)
          ),
          ev_ebitda: calculateDiff(
            target?.basic_stats?.ev_ebitda?.toFixed(1),
            medianBy(
              peers,
              (peer: any) => peer?.basic_stats?.ev_ebitda || 0,
              true
            )?.toFixed(1)
          ),
          pe: calculateDiff(
            target?.basic_stats?.pe?.toFixed(1),
            medianBy(
              peers,
              (peer: any) => peer?.basic_stats?.pe || 0,
              true
            )?.toFixed(1)
          ),
          pb: calculateDiff(
            target?.basic_stats?.pb?.toFixed(1),
            medianBy(
              peers,
              (peer: any) => peer?.basic_stats?.pb || 0,
              true
            )?.toFixed(1)
          ),
        },
        factset_stats: {
          revenue: calculateDiff(
            target?.factset_stats?.revenue?.toFixed(0),
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.revenue || 0
            )?.toFixed(0)
          ),
          revenue_1y: calculatePercentageDiff(
            target?.factset_stats?.revenue_1y,
            medianBy(peers, (peer: any) => peer?.factset_stats?.revenue_1y || 0)
          ),
          revenue_3y_cagr: calculatePercentageDiff(
            target?.factset_stats?.revenue_3y_cagr,
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.revenue_3y_cagr || 0
            )
          ),
          ebitda: calculateDiff(
            target?.factset_stats?.ebitda?.toFixed(0),
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.ebitda || 0
            )?.toFixed(0),
            true
          ),
          ebitda_1y: calculatePercentageDiff(
            target?.factset_stats?.ebitda_1y,
            medianBy(peers, (peer: any) => peer?.factset_stats?.ebitda_1y || 0)
          ),
          ebitda_3y_cagr: calculatePercentageDiff(
            target?.factset_stats?.ebitda_3y_cagr,
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.ebitda_3y_cagr || 0
            )
          ),
          net_income: calculateDiff(
            target?.factset_stats?.net_income?.toFixed(0),
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.net_income || 0
            )?.toFixed(0)
          ),
          net_income_1y: calculatePercentageDiff(
            target?.factset_stats?.net_income_1y,
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.net_income_1y || 0
            )
          ),
          net_income_3y_cagr: calculatePercentageDiff(
            target?.factset_stats?.net_income_3y_cagr,
            medianBy(
              peers,
              (peer: any) => peer?.factset_stats?.net_income_3y_cagr || 0
            )
          ),
        },
      },
    ];

    return [
      ...orderBy(peers, (peer) => peer?.basic_stats?.market_value_usd || 0, [
        "desc",
      ]).filter((peer) => peer?.basic_stats && peer?.factset_stats),
      ...basicCalculatedData,
      target,
      ...diffCalculatedData,
    ];
  }, [calculateDiff, calculatePercentageDiff, rawComparisons]);

  return {
    comparisons,
    rawComparisons,
    isLoading: isLoading || (!embed && mnaPeersComparisonLoading),
  };
};

export default function PeersTradeComparison({ embed }: any) {
  const router = useRouter();
  const entityId = router?.query["entity-id"] as string;

  const { comparisons, rawComparisons, isLoading } = usePeersComparisonData(
    entityId,
    embed
  );

  const [isExpanded, setIsExpanded] = useGlobalState(
    `mnaPeersIsExpanded`,
    true
  );

  const content = useMemo(() => {
    if (isLoading) {
      return <PeersTradeComparisonLoading />;
    }

    if (!isArray(rawComparisons)) return null;

    return (
      <div className="max-w-full overflow-x-auto">
        <Table
          columns={columns}
          dataSource={comparisons}
          tableClassName="!mb-0 !border-0"
          headRowClassName="!bg-white"
          headCellClassName="!border-r-0 !border-y-1 !border-solid !border-y-slate-200 !py-[10px]"
          rowClassName="!bg-white"
          cellClassName="!border-r-0 !border-y-1 !border-solid !border-y-slate-200 !py-[10px]"
          rowClassNameCallback={(record: any) => {
            if (!record) return "";

            if (record.type === "calculated") {
              return "!bg-inactive-gray/20 font-bold";
            }

            if (record.is_target) {
              return "!bg-smart-teal/20";
            }

            if (record.type === "diff") {
              return "italic";
            }

            return "!bg-white";
          }}
        />
      </div>
    );
  }, [comparisons, isLoading, rawComparisons]);

  if (!entityId) return null;

  return (
    <CustomCard className="overflow-x-hidden p-0 shadow-none border border-solid border-slate-200">
      <div className="flex items-center justify-between p-[15px] bg-intelligent-silver/10">
        <div className="flex items-center gap-[15px]">
          <div
            className="flex items-center justify-center w-[40px] h-[40px] rounded-full cursor-pointer hover:bg-intelligent-silver/20"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
            />
          </div>
          <div className="flex flex-col">
            <span className="flex flex-col md:flex-row md:items-center gap-[5px]">
              <h3 className="text-[20px] font-bold mb-0">
                Trading Peer Comparables{" "}
              </h3>
              {rawComparisons?.[0]?.created_at && (
                <span className="text-[12px]">
                  {" "}
                  As of{" "}
                  {format(
                    new Date(rawComparisons?.[0]?.created_at),
                    "dd MMM yyyy"
                  )}
                </span>
              )}
            </span>
            <span className="text-[12px]">
              Implied multiples based on reported transactions on a FY basis
            </span>
          </div>
        </div>
        {isLoading && !isExpanded && (
          <div className="flex items-center gap-[15px] mr-[10px]">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-[20px] text-smart-teal"
            />
          </div>
        )}
      </div>
      <div className={twMerge(!isExpanded && "hidden")}>{content}</div>
    </CustomCard>
  );
}
